/**
 * useAppNav — the signed-in user's navigation model, shared by the desktop
 * sidebar (Layout), the mobile More page and the ⌘K command palette, so all
 * three list exactly the same tools. Moved out of Layout.tsx verbatim (Sep 2026);
 * visibility still derives from tools.*.enabled + the role hooks.
 */
import { createContext, useContext, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { CalendarDays,
  Activity, Award, CalendarOff, ChartLine, Clock, ClipboardList, CreditCard, FileText, Folder, FolderKanban,
  BookOpen, FolderLock, House, IdCard, ListChecks, MessagesSquare, Network, Newspaper,
  Percent, Receipt, Repeat, Send, Settings, Share2, Signature, SlidersHorizontal, Target, UsersRound,
  type LucideIcon,
  Building2,
} from 'lucide-react';
import { useProfile } from '../profile/ProfileProvider';
import { useSharedTodos } from '../notifications/TodosContext';
import { todosSig } from '../notifications/seen';
import { useMyOnboardingCount } from '../onboarding/api';
import { useCanManageMutations } from '../mutations/access';
import { useIsManager } from '../myTeam/useIsManager';
import { useIsCommissionsAdmin } from '../commissions/useIsCommissionsAdmin';
import { useCommissionAccess } from '../commissions/useCommissionAccess';
import { useIsInvoiceApprover } from '../invoices/hooks/useIsInvoiceApprover';
import { useIsCreditCardApprover } from '../creditCards/hooks/useIsCreditCardApprover';
import { useIsProjectsAdmin } from '../projects/useIsProjectsAdmin';
import { ANALYTICS_DASHBOARDS, ANALYTICS_NAV_PINNED } from '../analytics/registry';
import { OPERATIONS_DASHBOARDS, OPERATIONS_NAV_PINNED } from '../operations/registry';
import { isAnalyticsAdmin, canViewAnalyticsPage, isSubscriptionAdmin, visibleSubscriptionPages, isOperationsAdmin, canViewOperationsPage } from '@daaily/shared';

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Exact match (Home). Tools match their whole subtree by default. */
  end?: boolean;
  /** Count badge (open asks) on a plain item. */
  badge?: number;
}

/** Lucide per module (design system icon mapping). */
export const ICONS = {
  home: House, time: Clock, absence: CalendarOff, org: Network, documents: Folder, drivers: Award,
  projects: FolderKanban, directors: CalendarDays, newsletter: Newspaper, outreach: Send, businessCard: IdCard, performance: MessagesSquare,
  onboarding: ListChecks, expenses: Receipt, invoices: FileText, cards: CreditCard, share: Share2,
  commissions: Percent, usl: Target, subscriptions: Repeat, analytics: ChartLine, operations: Activity,
  signatures: Signature, admin: Settings, config: SlidersHorizontal, fte: UsersRound, mutations: ClipboardList,
  vault: FolderLock, guide: BookOpen, crm: Building2,
} satisfies Record<string, LucideIcon>;

export function useAppNav() {
  const { state } = useProfile();
  const isInvoiceApprover = useIsInvoiceApprover();
  const isCreditCardApprover = useIsCreditCardApprover();
  const canApproveExpenses = state.status === 'ready' && (state.isAccounting || state.isAdmin);
  // Read-only Expenses Team view: expenses-provisioned + access role
  // Director/CEO-COO/Admin (or app admin). Mirrors the server gate.
  const canViewExpenseTeam =
    state.status === 'ready'
    && (state.isAdmin
      || ((state.user.tools?.expenses?.enabled === true || !!state.user.role)
        && ['director', 'ceo', 'admin'].includes(state.user.accessRole ?? '')));
  // Projects: only admins (tools.projects.role approver/approver_admin OR
  // app/hub admin — same set as the server) get the Personal|Team sub-nav;
  // contributors keep a plain single-destination row.
  const isProjectsAdmin = useIsProjectsAdmin();
  // A `signatory` may sign + see own docs but NOT send — no Team sub-nav.
  const canSendSignatures =
    state.status === 'ready' &&
    (state.isAdmin || (state.user.tools?.signatures?.enabled === true && state.user.tools.signatures.role !== 'signatory'));
  // Analytics sub-nav derives from the registry + the user's per-page grants:
  // admins see all pages + the Admin area; viewers see only granted pages.
  const analyticsAdmin =
    state.status === 'ready' && isAnalyticsAdmin({ isAppAdmin: state.isAdmin, role: state.user.tools?.analytics?.role });
  const analyticsGrants = state.status === 'ready' ? (state.user.analyticsPages ?? []) : [];
  // Subscriptions: viewers see the nav item only once ≥1 sub-nav is granted
  // (admins always). Enabled-but-nothing-granted → hidden (like analytics).
  const subscriptionVisible =
    state.status === 'ready' &&
    state.user.tools?.subscriptions?.enabled === true &&
    visibleSubscriptionPages(
      isSubscriptionAdmin({ isAppAdmin: state.isAdmin, role: state.user.tools?.subscriptions?.role }),
      state.user.subscriptionPages ?? [],
    ).length > 0;
  const analyticsNav = ANALYTICS_DASHBOARDS.filter((d) => !d.hidden)
    .filter((d) => (d.admin ? analyticsAdmin : canViewAnalyticsPage(analyticsAdmin, analyticsGrants, d.key)))
    .filter((d) => !d.navHiddenIfCanView || !canViewAnalyticsPage(analyticsAdmin, analyticsGrants, d.navHiddenIfCanView))
    // Pin Ask Data Questions · Reporting Center · Admin to the bottom (in that
    // order); everything else keeps registry order above them.
    .slice()
    .sort((a, b) => {
      const ra = ANALYTICS_NAV_PINNED.indexOf(a.key), rb = ANALYTICS_NAV_PINNED.indexOf(b.key);
      if (ra === -1 && rb === -1) return 0;
      if (ra === -1) return -1;
      if (rb === -1) return 1;
      return ra - rb;
    })
    .map((d) => ({ to: d.path ? `/analytics/${d.path}` : '/analytics', label: d.label, end: d.path === '', match: [] as string[] }));
  // Operations sub-nav — same registry + per-page-grant model as analytics.
  const operationsAdmin =
    state.status === 'ready' && isOperationsAdmin({ isAppAdmin: state.isAdmin, role: state.user.tools?.operations?.role });
  const operationsGrants = state.status === 'ready' ? (state.user.operationsPages ?? []) : [];
  const operationsNav = OPERATIONS_DASHBOARDS.filter((d) => !d.hidden)
    .filter((d) => (d.admin ? operationsAdmin : canViewOperationsPage(operationsAdmin, operationsGrants, d.key)))
    .slice()
    .sort((a, b) => {
      const ra = OPERATIONS_NAV_PINNED.indexOf(a.key), rb = OPERATIONS_NAV_PINNED.indexOf(b.key);
      if (ra === -1 && rb === -1) return 0;
      if (ra === -1) return -1;
      if (rb === -1) return 1;
      return ra - rb;
    })
    .map((d) => ({ to: d.path ? `/operations/${d.path}` : '/operations', label: d.label, end: d.path === '', match: [] as string[] }));
  const SUB_NAVS: Record<string, { to: string; label: string; end?: boolean; match: string[] }[]> = {
    '/time': [
      { to: '/time', label: 'Personal', end: true, match: [] },
      { to: '/time/team', label: 'Team', match: ['/time/approvals'] },
      // Admin side (Summaries) — admins only, like Absence.
      ...(state.status === 'ready' && state.isAdmin ? [{ to: '/time/summaries', label: 'Admin', match: [] as string[] }] : []),
    ],
    '/outreach': [
      { to: '/outreach', label: 'Compose', end: true, match: [] },
      { to: '/outreach/templates', label: 'Templates', match: [] },
      { to: '/outreach/campaigns', label: 'Campaigns', match: ['/outreach/campaigns/'] },
      { to: '/outreach/sent', label: 'Sent', match: [] },
    ],
    // Self-registering (web/src/analytics/registry.tsx) — the analytics
    // maintainer adds dashboards there without touching this file. Filtered by
    // the caller's per-page grants (analyticsNav above).
    '/analytics': analyticsNav,
    // Self-registering (web/src/operations/registry.tsx) — same model.
    '/operations': operationsNav,
    '/documents': [
      { to: '/documents', label: 'Personal', end: true, match: [] },
      { to: '/documents/team', label: 'Team', match: [] },
    ],
    '/signatures': [
      { to: '/signatures', label: 'Personal', end: true, match: [] },
      ...(canSendSignatures ? [{ to: '/signatures/team', label: 'Team', match: [] as string[] }] : []),
      // Template management — template admins (app admin or signatures approver_admin).
      ...(state.status === 'ready' && (state.isAdmin || state.user.tools?.signatures?.role === 'approver_admin')
        ? [{ to: '/signatures/templates', label: 'Templates', match: [] as string[] }, { to: '/signatures/stamps', label: 'Stamps', match: [] as string[] }]
        : []),
    ],
    '/document-vault': [
      { to: '/document-vault', label: 'Employee', end: true, match: [] },
      { to: '/document-vault/company', label: 'Company', match: [] },
    ],
    '/absence': [
      { to: '/absence', label: 'Personal', end: true, match: ['/absence/calendar'] },
      { to: '/absence/approvals', label: 'Team', match: ['/absence/team-calendar', '/absence/member'] },
      // Admin side (Summaries + Warnings) — admins only, split out Sep 2026.
      ...(state.status === 'ready' && state.isAdmin
        ? [{ to: '/absence/summaries', label: 'Admin', match: ['/absence/warnings', '/absence/carry-over'] as string[] }]
        : []),
    ],
    // Projects: Team = the full portfolio, admins only (contributors keep a
    // plain row → straight to their participating-only Personal view).
    ...(isProjectsAdmin ? {
      '/projects': [
        { to: '/projects', label: 'Personal', end: true, match: ['/projects/items', '/projects/requests', '/projects/timeline'] },
        { to: '/projects/team', label: 'Team', match: [] as string[] },
        { to: '/projects/admin', label: 'Admin', match: [] as string[] },
      ],
    } : {}),
    // Finance tools: the Team side is the Approvals queue — children only for
    // people who can actually approve (everyone else keeps a plain item).
    // Expenses: Admin = the approvals queue (renamed from "Team", Sep 2026);
    // Team = the read-only org-leader view (access role director/ceo/admin).
    ...((canApproveExpenses || canViewExpenseTeam) ? {
      '/expenses': [
        { to: '/expenses', label: 'Personal', end: true, match: ['/expenses/resources', '/expenses/allowances', '/expenses/statements'] },
        ...(canViewExpenseTeam ? [{ to: '/expenses/team', label: 'Team', match: [] as string[] }] : []),
        ...(canApproveExpenses ? [{ to: '/expenses/approvals', label: 'Admin', match: [] as string[] }] : []),
      ],
    } : {}),
    ...(isInvoiceApprover ? {
      '/invoices': [
        { to: '/invoices', label: 'Personal', end: true, match: ['/invoices/resources', '/invoices/new'] },
        { to: '/invoices/approvals', label: 'Team', match: [] },
      ],
    } : {}),
    ...(isCreditCardApprover ? {
      '/credit-cards': [
        { to: '/credit-cards', label: 'Personal', end: true, match: ['/credit-cards/library', '/credit-cards/new', '/credit-cards/statements'] },
        { to: '/credit-cards/approvals', label: 'Team', match: [] },
      ],
    } : {}),
  };
  // Team-side action counts per tool route (approval queues from the shared
  // todos aggregator) → count badges on the "Team" sub-items / parent rows.
  const todos = useSharedTodos();
  const teamCounts = useMemo(() => {
    const m: Record<string, number> = {};
    for (const t of todos) if (t.teamRoute) m[t.teamRoute] = (m[t.teamRoute] ?? 0) + 1;
    return m;
  }, [todos]);
  // Badge content signature per tool route (ids + labels of the to-dos behind
  // it): a badge hides once its queue is visited and returns when this changes.
  const teamSigs = useMemo(() => {
    const m: Record<string, typeof todos> = {};
    for (const t of todos) if (t.teamRoute) (m[t.teamRoute] ??= []).push(t);
    return Object.fromEntries(Object.entries(m).map(([k, items]) => [k, todosSig(items)]));
  }, [todos]);
  const onboardingSig = useMemo(() => todosSig(todos.filter((t) => t.kind === 'onboarding')), [todos]);

  const { pathname } = useLocation();
  const ready = state.status === 'ready';
  const isAdmin = ready && state.isAdmin;
  const tools = ready ? state.user.tools : undefined;
  const onbCount = useMyOnboardingCount(ready ? state.user.email.toLowerCase() : '');
  const canManageMutations = useCanManageMutations();
  const isManager = useIsManager();
  const isCommissionsAdmin = useIsCommissionsAdmin();
  const commissionsEnabled = !!tools?.commissions?.enabled;
  const commissionsLive = ready && state.config.commissionsLive === true;
  // App admins get Commissions in the admin group (below Monthly Mutations);
  // everyone else sees it in the general tools list — BDs only once it's live.
  const commissionsInAdminNav = commissionsEnabled && isAdmin;
  const commissionsInToolNav = commissionsEnabled && !isAdmin && (commissionsLive || isCommissionsAdmin);
  // People on a person-specific bonus plan (the "other" layer) see the tool as Bonus, not Commissions.
  const commissionAccess = useCommissionAccess();
  const commissionsIsBonus = !isAdmin && !isCommissionsAdmin && commissionAccess.hasOtherLayer && !commissionAccess.isBd && !commissionAccess.hasAgentLayer;
  const commissionsLabel = commissionsIsBonus ? 'Bonus' : 'Commissions';
  // Admin sidebar item active across /admin/* EXCEPT /admin/config (its own item).
  const adminActive =
    pathname === '/admin' ||
    (pathname.startsWith('/admin/') &&
      !pathname.startsWith('/admin/config') &&
      !pathname.startsWith('/admin/fte') &&
      !pathname.startsWith('/admin/mutations') &&
      !pathname.startsWith('/admin/onboarding') &&
      !pathname.startsWith('/admin/performance-reviews'));

  // Sidebar order (Aug 2026, per Michael): HR/people tools first, then the
  // finance tools, then the data dashboards. Grouped with labels in the design
  // system (Sep 2026); order inside each group is unchanged.
  // DAAily CRM test (separate app; /crm links out). Super admins only for now.
  const crmVisible = state.status === 'ready' && (state.user.hubRole === 'super_admin' || state.user.hubRole === 'admin');
  const peopleNav: NavItem[] = [
    ...(tools?.timeTracking?.enabled ? [{ to: '/time', label: 'Time Tracking', icon: ICONS.time }] : []),
    ...(tools?.absence?.enabled ? [{ to: '/absence', label: 'Absence Management', icon: ICONS.absence }] : []),
    ...(tools?.orgChart?.enabled ? [{ to: '/org', label: 'Org Chart', icon: ICONS.org }] : []),
    ...(tools?.documents?.enabled ? [{ to: '/documents', label: 'Documents', icon: ICONS.documents }] : []),
    ...(tools?.drivers?.enabled ? [{ to: '/drivers', label: 'DAAily Drivers', icon: ICONS.drivers }] : []),
    ...(tools?.performanceReviews?.enabled ? [{ to: '/performance-reviews', label: 'Performance Reviews', icon: ICONS.performance }] : []),
    // Dynamic "Checklists" — appears while the user is on an active on-, offboarding or parental
    // checklist (employee, task owner or a person HR added), badge = their open tasks.
    ...(ready && onbCount.lists > 0 ? [{ to: '/onboarding', label: 'Checklists', icon: ICONS.onboarding, badge: onbCount.open || undefined }] : []),
    ...(tools?.signatures?.enabled ? [{ to: '/signatures', label: 'Signatures', icon: ICONS.signatures }] : []),
    // Commissions / Bonus is a People item (the person's own pay), not a revenue dashboard (13 Oct 2026).
    ...(commissionsInToolNav ? [{ to: '/commissions', label: commissionsLabel, icon: ICONS.commissions }] : []),
  ];
  const workNav: NavItem[] = [
    ...(tools?.directors?.enabled ? [{ to: '/directors', label: "Directors' Room", icon: ICONS.directors }] : []),
    ...(tools?.projects?.enabled ? [{ to: '/projects', label: 'Projects', icon: ICONS.projects }] : []),
    ...(tools?.newsletter?.enabled ? [{ to: '/newsletter', label: 'Internal Newsletters', icon: ICONS.newsletter }] : []),
    ...(tools?.outreach?.enabled ? [{ to: '/outreach', label: 'Outreach', icon: ICONS.outreach }] : []),
    ...(tools?.businessCard?.enabled ? [{ to: '/business-card', label: 'Business Card', icon: ICONS.businessCard }] : []),
    ...(crmVisible ? [{ to: '/crm', label: 'DAAily CRM', icon: ICONS.crm }] : []),
  ];
  const financeNav: NavItem[] = [
    ...(tools?.expenses?.enabled ? [{ to: '/expenses', label: 'Expense Tool', icon: ICONS.expenses }] : []),
    ...(tools?.invoices?.enabled ? [{ to: '/invoices', label: 'Invoice Submission', icon: ICONS.invoices }] : []),
    ...(tools?.creditCards?.enabled ? [{ to: '/credit-cards', label: 'Credit Cards', icon: ICONS.cards }] : []),
    ...(ready && !tools?.creditCards?.enabled ? [{ to: '/share-receipt', label: 'Share credit card receipt', icon: ICONS.share }] : []),
  ];
  const insightsNav: NavItem[] = [
    ...(tools?.usl?.enabled ? [{ to: '/usl', label: 'USL', icon: ICONS.usl }] : []),
    ...(subscriptionVisible ? [{ to: '/subscriptions', label: 'Subscriptions', icon: ICONS.subscriptions }] : []),
    // Analytics: viewers see the item only once at least one dashboard is
    // granted (admins always). Enabled-but-nothing-provisioned → hidden.
    ...(tools?.analytics?.enabled && analyticsNav.length > 0 ? [{ to: '/analytics', label: 'Analytics', icon: ICONS.analytics }] : []),
    // Operations: viewers see the item only once at least one page is granted
    // (admins always). Enabled-but-nothing-provisioned → hidden (like analytics).
    ...(tools?.operations?.enabled && operationsNav.length > 0 ? [{ to: '/operations', label: 'Operations', icon: ICONS.operations }] : []),
  ];
  const navGroups: { label: string; items: NavItem[] }[] = [
    { label: 'People', items: peopleNav },
    { label: 'Work', items: workNav },
    { label: 'Finance', items: financeNav },
    { label: 'Revenue & insights', items: insightsNav },
  ].filter((g) => g.items.length > 0);

  /** Admin-area destinations, in sidebar order (for More + ⌘K; the sidebar renders its own rows). */
  const adminItems: NavItem[] = [
    ...(isAdmin ? [
      { to: '/admin', label: 'Admin', icon: ICONS.admin },
      { to: '/admin/config', label: 'Config', icon: ICONS.config },
      { to: '/admin/fte', label: 'FTE', icon: ICONS.fte },
      { to: '/admin/mutations', label: 'Monthly Mutations', icon: ICONS.mutations },
      ...(commissionsInAdminNav ? [{ to: '/commissions', label: 'Commissions', icon: ICONS.commissions }] : []),
      { to: '/admin/performance-reviews', label: 'Performance Reviews', icon: ICONS.performance },
      { to: '/admin/onboarding', label: 'On/Offboarding', icon: ICONS.onboarding },
      { to: '/document-vault', label: 'Document Vault', icon: ICONS.vault },
    ] : []),
    ...(ready && !isAdmin && canManageMutations ? [{ to: '/admin/mutations', label: 'Monthly Mutations', icon: ICONS.mutations }] : []),
  ];

  return {
    isInvoiceApprover, isCreditCardApprover, canApproveExpenses, canViewExpenseTeam, isProjectsAdmin, canSendSignatures, analyticsAdmin, analyticsGrants, subscriptionVisible, analyticsNav, operationsAdmin, operationsGrants, operationsNav, SUB_NAVS, todos, teamCounts, teamSigs, onboardingSig, pathname, ready, isAdmin, tools, onbCount, canManageMutations, isManager, isCommissionsAdmin, commissionsEnabled, commissionsLive, commissionsInAdminNav, commissionsInToolNav, adminActive, peopleNav, workNav, financeNav, insightsNav, navGroups, adminItems,
  };
}

export type AppNav = ReturnType<typeof useAppNav>;
/** Layout computes the nav once and provides it; More + ⌘K read it here (no second set of listeners). */
export const AppNavContext = createContext<AppNav | null>(null);
export function useSharedAppNav(): AppNav {
  const v = useContext(AppNavContext);
  if (!v) throw new Error('useSharedAppNav must be used inside <Layout>');
  return v;
}
